import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Info } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import {
  areaClusters,
  areaFarms,
  areaScopeKinds,
  roleLabel,
  scopesForManager,
  validateAccessDraft,
  type ManageArea,
  type ScopeKind,
} from '@/pages/team/teamLogic';
import { useUpdateMember } from '@/pages/team/hooks/useTeamQueries';
import { useRoles } from '@/pages/company/roles/hooks/useRolesQueries';
import type { ClusterOption, FarmOption, UserMember } from '@/pages/team/types';
import { TeamRoleSelect, type RoleSelection } from './TeamRoleSelect';
import { ScopeSelector } from './ScopeSelector';

interface ChangeAccessDrawerProps {
  member: UserMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clusters: ClusterOption[];
  farms: FarmOption[];
  /** AK-886: роли, которые можно выдать (`grantableRoles(can_invite_codes)`). */
  roles: readonly string[];
  /** AK-886: область управляющего; нет — без ограничений. */
  area?: ManageArea | null;
  /** Группа «Роли компании» (кастомные) — только админу компании (AK-914). */
  allowCustom: boolean;
}

function memberScope(m: UserMember): { scope: ScopeKind; scopeId: string | null } {
  if (m.cluster_id) return { scope: 'cluster', scopeId: m.cluster_id };
  if (m.farm_id) return { scope: 'farm', scopeId: m.farm_id };
  return { scope: 'company', scopeId: null };
}

function scopeValueLabel(
  scope: ScopeKind,
  scopeId: string | null,
  clusters: ClusterOption[],
  farms: FarmOption[],
): string {
  if (scope === 'company') return 'Вся компания';
  if (scope === 'cluster') {
    return clusters.find((c) => c.id === scopeId)?.name ?? 'Кластер';
  }
  return farms.find((f) => f.id === scopeId)?.name ?? 'Хозяйство';
}

/** Компаратор «Сейчас → Станет». */
function Compare({ now, next }: { now: string; next: string }) {
  const changed = now !== next;
  return (
    <div className="flex items-stretch gap-2">
      <div className="flex-1 rounded-lg border border-border bg-background px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Сейчас
        </div>
        <div className="text-sm font-medium">{now}</div>
      </div>
      <div className="flex items-center text-muted-foreground">
        <ArrowRight className="h-4 w-4" />
      </div>
      <div
        className={
          changed
            ? 'flex-1 rounded-lg border border-accent-brand/50 bg-accent-brand/10 px-3 py-2'
            : 'flex-1 rounded-lg border border-border bg-background px-3 py-2'
        }
      >
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Станет
        </div>
        <div className="text-sm font-medium">{next}</div>
      </div>
    </div>
  );
}

export function ChangeAccessDrawer({
  member,
  open,
  onOpenChange,
  clusters,
  farms,
  roles,
  area,
  allowCustom,
}: ChangeAccessDrawerProps) {
  const update = useUpdateMember();
  const rolesQ = useRoles();
  const [selection, setSelection] = useState<RoleSelection | null>(null);
  const [scope, setScope] = useState<ScopeKind>('company');
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();

  // Системная роль (строка) — для scope-логики (кастомные scope-независимы в UI).
  const role = selection?.kind === 'system' ? selection.code : '';
  const isCustom = selection?.kind === 'custom';

  const initial = useMemo(
    () => (member ? { role: member.role, ...memberScope(member) } : null),
    [member],
  );

  useEffect(() => {
    if (open && initial) {
      setSelection({ kind: 'system', code: initial.role });
      setScope(initial.scope);
      setScopeId(initial.scopeId);
      setScopeError(undefined);
      setSubmitError(undefined);
    }
  }, [open, initial]);

  // Системная роль сменилась → откат несовместимого scope (с ролью или
  // областью управляющего). Кастомные — не трогаем.
  useEffect(() => {
    if (!role) return;
    const allowed = scopesForManager(role, area);
    if (allowed.length > 0 && !allowed.includes(scope)) {
      setScope(allowed[0]);
      setScopeId(null);
      setScopeError(undefined);
    }
  }, [role, scope, area]);

  if (!member || !initial) return null;

  const displayName =
    member.display_name ||
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.email;

  const customRoles = rolesQ.data?.custom_roles ?? [];
  const customRoleName =
    selection?.kind === 'custom'
      ? (customRoles.find((r) => r.id === selection.roleId)?.name ?? 'Кастомная роль')
      : '';

  const dirty = isCustom
    ? true // кастомная роль — всегда отличается от системной (member.role строковый)
    : role !== initial.role ||
      scope !== initial.scope ||
      (scope !== 'company' && scopeId !== initial.scopeId);

  const submit = async () => {
    setSubmitError(undefined);
    try {
      if (isCustom && selection.kind === 'custom') {
        // Кастомная роль: назначаем по role_id; зона участника сохраняется как есть.
        await update.mutateAsync({
          memberId: member.member_id,
          payload: { role_id: selection.roleId },
        });
      } else {
        const v = validateAccessDraft({ role, scope, scopeId });
        setScopeError(v.scopeError);
        if (!v.ok) return;
        await update.mutateAsync({
          memberId: member.member_id,
          payload: { role, scope, scope_id: scope === 'company' ? null : scopeId },
        });
      }
      onOpenChange(false);
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.detail : 'Не удалось сохранить изменения');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[480px] overflow-y-auto">
        <SheetTitle>Доступ участника</SheetTitle>
        <SheetDescription>
          <span className="font-medium text-foreground">{displayName}</span> ·{' '}
          {member.email}
        </SheetDescription>

        <div className="mt-6 space-y-5">
          <div className="space-y-2">
            <Label>Роль</Label>
            <Compare
              now={roleLabel(initial.role)}
              next={isCustom ? customRoleName : roleLabel(role)}
            />
            <TeamRoleSelect
              value={selection}
              onChange={setSelection}
              roles={roles}
              allowCustom={allowCustom}
              currentRole={initial.role}
            />
          </div>

          {!isCustom && (
            <div className="space-y-2">
              <Label>Зона доступа</Label>
              <Compare
                now={scopeValueLabel(initial.scope, initial.scopeId, clusters, farms)}
                next={scopeValueLabel(scope, scopeId, clusters, farms)}
              />
              <ScopeSelector
                role={role}
                scope={scope}
                scopeId={scopeId}
                clusters={areaClusters(clusters, area)}
                farms={areaFarms(farms, area)}
                visibleScopes={areaScopeKinds(area)}
                onScopeChange={(s) => {
                  setScope(s);
                  setScopeId(null);
                  setScopeError(undefined);
                }}
                onScopeIdChange={(id) => {
                  setScopeId(id);
                  setScopeError(undefined);
                }}
                error={scopeError}
              />
            </div>
          )}

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Доступ обновится <b>сразу после сохранения</b> — переприглашать и
              просить перелогиниться не нужно.
            </AlertDescription>
          </Alert>

          {submitError && (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="mt-6 flex gap-3 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Отмена
          </Button>
          <Button
            className="flex-1"
            onClick={submit}
            disabled={update.isPending || !dirty}
          >
            {update.isPending ? 'Сохраняем…' : 'Сохранить изменения'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
